import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/services/supabaseClient";
import type { Tables, InsertTables, UpdateTables } from "@/types/database";

async function fetchCBMContacts() {
  const { data, error } = await supabase
    .from("cbm_contacts")
    .select("*")
    .eq("is_active", true)
    .order("role")
    .order("full_name");

  if (error) throw error;
  return data as Tables<"cbm_contacts">[];
}

async function fetchCBMContactById(id: string) {
  const { data, error } = await supabase
    .from("cbm_contacts")
    .select("*")
    .eq("id", id)
    .single();

  if (error) throw error;
  return data as Tables<"cbm_contacts">;
}

async function fetchCBMContactsByRole(role: string) {
  const { data, error } = await supabase
    .from("cbm_contacts")
    .select("*")
    .eq("role", role)
    .eq("is_active", true)
    .order("full_name");

  if (error) throw error;
  return data as Tables<"cbm_contacts">[];
}

async function createCBMContact(contact: InsertTables<"cbm_contacts">) {
  const { data, error } = await supabase
    .from("cbm_contacts")
    .insert([contact])
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function updateCBMContact(
  id: string,
  updates: UpdateTables<"cbm_contacts">,
) {
  const { data, error } = await supabase
    .from("cbm_contacts")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function deleteCBMContact(id: string) {
  const { error } = await supabase.from("cbm_contacts").delete().eq("id", id);

  if (error) throw error;
}

export function useCBMContacts() {
  return useQuery({
    queryKey: ["cbm_contacts"],
    queryFn: fetchCBMContacts,
  });
}

export function useCBMContact(id: string) {
  return useQuery({
    queryKey: ["cbm_contact", id],
    queryFn: () => fetchCBMContactById(id),
    enabled: !!id,
  });
}

export function useCBMContactsByRole(role: string) {
  return useQuery({
    queryKey: ["cbm_contacts", "role", role],
    queryFn: () => fetchCBMContactsByRole(role),
    enabled: !!role,
  });
}

export function useCreateCBMContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCBMContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cbm_contacts"] });
    },
  });
}

export function useUpdateCBMContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: UpdateTables<"cbm_contacts">;
    }) => updateCBMContact(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cbm_contacts"] });
      queryClient.invalidateQueries({ queryKey: ["cbm_contact"] });
    },
  });
}

export function useDeleteCBMContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCBMContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cbm_contacts"] });
    },
  });
}
